import { type MetaDescriptor } from '@remix-run/node'
import { appName } from './constants.ts'

/** 1200x630 card rendered from the logo, see public/img/og-image.png */
export const ogImagePath = '/img/og-image.png'
export const ogImageAlt = `${appName} — Helping businesses create abundance`

type RequestInfo = { origin: string; path: string }

/**
 * Pulls the request info out of the root loader so nested routes can build
 * absolute URLs. Crawlers reject relative og:image values.
 */
export function getRequestInfo(
	matches: Array<{ id: string; data?: unknown }>,
): RequestInfo | undefined {
	const root = matches.find(match => match.id === 'root')
	return (root?.data as { requestInfo?: RequestInfo } | undefined)?.requestInfo
}

/**
 * Title, description and the Open Graph / Twitter tags that go with them.
 * Remix only renders the deepest route's meta, so every route that defines
 * `meta` has to go through this or it loses its social card.
 */
export function socialMeta({
	title,
	description,
	requestInfo,
}: {
	title: string
	description: string
	requestInfo?: RequestInfo
}): MetaDescriptor[] {
	const origin = requestInfo?.origin
	const image = origin ? `${origin}${ogImagePath}` : ogImagePath

	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:site_name', content: appName },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:image', content: image },
		{ property: 'og:image:type', content: 'image/png' },
		{ property: 'og:image:width', content: '1200' },
		{ property: 'og:image:height', content: '630' },
		{ property: 'og:image:alt', content: ogImageAlt },
		...(origin && requestInfo
			? [{ property: 'og:url', content: `${origin}${requestInfo.path}` }]
			: []),
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:image', content: image },
		{ name: 'twitter:image:alt', content: ogImageAlt },
	]
}
